import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { SharedPanelProps } from '~/eds/src/types/SharedPanelProps';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';

import { ConversationHistory } from './ConversationHistory/ConversationHistory';
import { QuestionGroups } from './QuestionGroups/QuestionGroups';
import { Questions } from './Questions/Questions';

export type SideContentTabs =
  | 'conversationHistory'
  | 'questions'
  | 'questionGroups';

interface UseChatBotSideContent {
  sideContent: SharedPanelProps['sideContent'];
}
type UseChatBotSideContentProps = {
  disableRun?: boolean;
};

export const useChatBotSideContent = ({
  disableRun,
}: UseChatBotSideContentProps): UseChatBotSideContent => {
  const intl = useIntl();
  const isVisible = useSelector(
    slices.chatbot.selectors.selectIsSideContentVisible,
  );
  const selectedTab = useSelector(
    slices.chatbot.selectors.selectSideContentTab,
  );
  const hasConversationHistory = useFlag(
    FlagType.AskAnythingConversationHistory,
  );
  const dispatch = useDispatch();

  const tabs = [
    {
      label: intl.formatMessage({
        id: 'UseChatBotSideContent.historyTab',
        defaultMessage: 'History',
        description: 'Tab label for conversation history',
      }),
      panel: <ConversationHistory />,
      value: 'conversationHistory',
      hidden: !hasConversationHistory,
    },
    {
      label: intl.formatMessage({
        id: 'UseChatBotSideContent.savedQuestionsTab',
        defaultMessage: 'Saved Questions',
        description: 'Tab label for saved questions',
      }),
      panel: <Questions disableRun={disableRun} />,
      value: 'questions',
    },
    {
      label: intl.formatMessage({
        id: 'UseChatBotSideContent.questionGroupsTab',
        defaultMessage: 'Question Groups',
        description: 'Tab label for question groups',
      }),
      panel: <QuestionGroups disableRun={disableRun} />,
      value: 'questionGroups',
    },
  ];

  return {
    sideContent: {
      action: {
        onClick: () => {
          // Default to conversation history if the FF is enabled.
          if (hasConversationHistory) {
            dispatch(
              slices.chatbot.actions.setSideContentTab('conversationHistory'),
            );
          }
          dispatch(slices.chatbot.actions.toggleSideContent());
        },
        icon: 'left-panel',
        text: intl.formatMessage({
          id: 'UseChatBotSideContent.showHideSideContentText',
          defaultMessage: 'Show/Hide side content',
          description: 'Accessibility text for show/hide side content button',
        }),
        tooltip: isVisible
          ? intl.formatMessage({
              id: 'UseChatBotSideContent.closePanelTooltip',
              defaultMessage: 'Close left panel',
              description: 'Tooltip for closing the left panel',
            })
          : intl.formatMessage({
              id: 'UseChatBotSideContent.openPanelTooltip',
              defaultMessage: 'Open left panel',
              description: 'Tooltip for opening the left panel',
            }),
      },
      tabs: {
        selectedTab,
        onSelectTab: (updatedTab) =>
          dispatch(
            slices.chatbot.actions.setSideContentTab(
              updatedTab as SideContentTabs,
            ),
          ),
        tabs: tabs.filter((tab) => !tab.hidden),
      },
      isVisible,
    },
  };
};
